import { useState } from "react";
import { Tag, Plus, Trash2, Pencil, Share2, ChevronUp } from "lucide-react";
import TagSharePanel from "./TagSharePanel";
import type { ITag } from "../../types";

const COLOR_PRESETS = [
  "#3b82f6", // blue
  "#10b981", // green
  "#f59e0b", // amber
  "#ef4444", // red
  "#8b5cf6", // purple
  "#ec4899", // pink
  "#06b6d4", // cyan
  "#f97316", // orange
];

interface TagManagerProps {
  tags: ITag[];
  createTag: (name: string, color: string) => Promise<ITag | null>;
  updateTag: (id: string, updates: { name?: string; color?: string }) => Promise<void>;
  deleteTag: (id: string) => Promise<void>;
}

export default function TagManager({ tags, createTag, updateTag, deleteTag }: TagManagerProps) {
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(COLOR_PRESETS[0]);
  const [editingTag, setEditingTag] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [shareExpandedTag, setShareExpandedTag] = useState<string | null>(null);

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    await createTag(newTagName.trim(), newTagColor);
    setNewTagName("");
    setNewTagColor(COLOR_PRESETS[0]);
  };

  const handleUpdateTag = async (id: string) => {
    if (!editName.trim()) return;
    await updateTag(id, { name: editName.trim() });
    setEditingTag(null);
  };

  return (
    <div className="bg-surface border border-border rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Tag className="w-5 h-5 text-muted-foreground" />
        <h3 className="text-sm font-medium text-foreground">Tags</h3>
      </div>

      {/* Tag list */}
      <div className="space-y-2">
        {tags.map((tag) => (
          <div key={tag.id}>
            <div className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: tag.color }}
              />
              {editingTag === tag.id ? (
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleUpdateTag(tag.id);
                    if (e.key === "Escape") setEditingTag(null);
                  }}
                  onBlur={() => handleUpdateTag(tag.id)}
                  autoFocus
                  className="flex-1 rounded border border-input bg-background px-2 py-0.5 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              ) : (
                <span className="flex-1 text-sm text-foreground">{tag.name}</span>
              )}
              <button
                onClick={() => {
                  setEditingTag(tag.id);
                  setEditName(tag.name);
                }}
                className="p-1 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() =>
                  setShareExpandedTag(shareExpandedTag === tag.id ? null : tag.id)
                }
                className="p-1 text-muted-foreground hover:text-primary transition-colors"
              >
                {shareExpandedTag === tag.id ? (
                  <ChevronUp className="w-3.5 h-3.5" />
                ) : (
                  <Share2 className="w-3.5 h-3.5" />
                )}
              </button>
              <button
                onClick={() => deleteTag(tag.id)}
                className="p-1 text-muted-foreground hover:text-destructive transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            {shareExpandedTag === tag.id && <TagSharePanel tag={tag} />}
          </div>
        ))}
      </div>

      {/* New tag form */}
      <div className="flex items-center gap-2 pt-2 border-t border-border">
        <div className="flex gap-1">
          {COLOR_PRESETS.map((color) => (
            <button
              key={color}
              onClick={() => setNewTagColor(color)}
              className={`w-5 h-5 rounded-full transition-all ${
                newTagColor === color ? "ring-2 ring-primary ring-offset-1" : ""
              }`}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
        <input
          type="text"
          value={newTagName}
          onChange={(e) => setNewTagName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreateTag()}
          placeholder="New tag..."
          className="flex-1 rounded border border-input bg-background px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        <button
          onClick={handleCreateTag}
          disabled={!newTagName.trim()}
          className="p-1 text-primary hover:text-primary/80 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
